import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Formik, FormikHelpers } from 'formik'
import { omit } from 'ramda'
import { Stack, Heading, useResponsiveValue, useToast } from '@machobear/design-system'

import { useSponsoredListingCreditInformation } from '@/hooks/use-block-and-pay'
import FeaturesSection from './features-section'
import LandlordSection from './landlord-section'
import InfoSection from './info-section'
import TermsSection from './terms-section'
import SponsoredListing from '../components/sponsored-listing'
import FormFooter from '../components/footer'
import { DetailsFormValues, Listing, ListingLandlords } from '../types'
import FormWrapper from '../form-wrapper'
import { detailsValidationSchema } from '../util/vallidationSchema'
import { formatLandlordData } from '../util/landlords'
import { getParkingStorageType } from '../util'
import useRedirectPublishedListing from '../hooks/use-redirect-published-listing'
import { useLandlordValues } from '../hooks/use-landlord-values'
import { useBoostingPackages } from '../hooks/use-boosting-packages'

const ListingDetailsForm = ({
  data,
  landlordData,
  isLoading
}: {
  data: Listing
  landlordData: ListingLandlords
  isLoading: boolean
}) => {
  const { t } = useTranslation()
  const history = useHistory()
  const dispatch = useDispatch()
  const showToast = useToast()
  const userId = useSelector((state: any) => state.profile?.id)

  const isMobile = useResponsiveValue()({
    mobile: true,
    tablet: false
  })

  useRedirectPublishedListing(data)

  const usage = useSponsoredListingCreditInformation()
  const { primaryLandlord, setPrimaryLandlord, secondaryLandlords, setSecondaryLandlords } =
    useLandlordValues(landlordData)

  const formattedValues = omit(['videos', 'state_machine'], data)
  const parkingType = getParkingStorageType(formattedValues?.parking?.fee_frequency_txt_id)
  const storageType = getParkingStorageType(formattedValues?.storage?.fee_frequency_txt_id)
  const initialValues: DetailsFormValues = {
    ...(formattedValues as Listing),
    isBoostYourListing: false,
    duration: '',
    parkingType,
    storageType,
    // it was requested that we don't display zero (0) values in these fields
    allow_cats: data.allow_cats || '',
    allow_dogs: data.allow_dogs || ''
  }

  const saveLandlords = async () => {
    const { formattedLandlordData, removedSelfFromListing } = formatLandlordData({
      listingId: data.listing_id,
      userId,
      primaryLandlord,
      secondaryLandlords
    })
    const response = await dispatch.listing.updateLandlords(formattedLandlordData, {
      noLoading: true
    })
    return { landlordResponse: response, removedSelfFromListing }
  }

  const saveListing = async (values: DetailsFormValues) => {
    const { response } = await dispatch.listing.updateUnit(values)
    return response
  }

  const handleSaveDraft = async (
    values: DetailsFormValues,
    setSubmitting: FormikHelpers<DetailsFormValues>['setSubmitting']
  ) => {
    setSubmitting(true)
    const response = await saveListing(values)

    if (!response?.ok) {
      showToast({ message: 'Unable to save listing (draft)', tone: 'critical' })
      setSubmitting(false)
      return
    }

    const { landlordResponse } = await saveLandlords()

    if (!landlordResponse?.ok) {
      showToast({ message: 'Unable to save landlord data', tone: 'critical' })
      setSubmitting(false)
      return
    }

    showToast({
      message: t('l.listing.edit.listing_edited_toast_text', 'Your listing has been saved!'),
      tone: 'positive'
    })
    history.push('/listings')
    setSubmitting(false)
  }

  const onSubmit = async (
    values: DetailsFormValues,
    { setSubmitting }: FormikHelpers<DetailsFormValues>
  ) => {
    const response = await saveListing(values)

    if (!response.ok) {
      showToast({ message: 'Unable to save listing', tone: 'critical' })
      setSubmitting(false)
      return
    }

    const { landlordResponse, removedSelfFromListing } = await saveLandlords()

    if (!landlordResponse.ok) {
      showToast({ message: 'Unable to save landlord data', tone: 'critical' })
      setSubmitting(false)
      return
    }

    if (landlordResponse.ok) {
      removedSelfFromListing
        ? history.push('/listings')
        : history.push(`/listing/create/${data.listing_id}/media`)
    }
    setSubmitting(false)
  }

  const { packages } = useBoostingPackages()

  return (
    <>
      <Formik
        onSubmit={onSubmit}
        initialValues={initialValues}
        validationSchema={detailsValidationSchema}
        enableReinitialize
      >
        {({ values, isSubmitting, setSubmitting, setFieldValue }) => {
          return (
            <FormWrapper>
              <Form>
                <Stack align="left" space={{ mobile: 'gutter', desktop: 'large' }}>
                  {!isMobile && (
                    <Heading level="4" component="h2">
                      {t('Listing Details', 'Listing Details')}
                    </Heading>
                  )}
                  <InfoSection isLoading={isLoading} />
                  <TermsSection isLoading={isLoading} />
                  <FeaturesSection isLoading={isLoading} />
                  <SponsoredListing
                    isLoading={isLoading}
                    packages={packages}
                    availableCredits={usage.availableCredits}
                    onSelectPackage={(pkg: any) => {
                      setFieldValue('duration', pkg?.plan_name)
                      if (pkg?.plan_name) {
                        localStorage.setItem('selectedBoostingPackage', pkg?.plan_name)
                      } else {
                        localStorage.removeItem('selectedBoostingPackage')
                      }
                    }}
                  />
                  <LandlordSection
                    isLoading={isLoading}
                    primaryLandlord={primaryLandlord}
                    secondaryLandlords={secondaryLandlords}
                    setPrimaryLandlord={setPrimaryLandlord}
                    setSecondaryLandlords={setSecondaryLandlords}
                  />
                  <FormFooter
                    isLoading={isLoading}
                    onSave={() => handleSaveDraft(values, setSubmitting)}
                    isSubmitting={isSubmitting}
                    step="listing-details"
                  />
                </Stack>
              </Form>
            </FormWrapper>
          )
        }}
      </Formik>
    </>
  )
}

export default ListingDetailsForm
